import type { Casework } from "building-types/casework/caseworkTypes";
import type {
  FloorAssemblyFinishLayerOperator,
  SourcedFloorAssemblyFinishLayerOperator,
} from "building-types/floors/floorFinishTypes";
import { FloorFinishType } from "building-types/floors/floorFinishTypes";
import type { Prop, PropAttributeTable } from "building-types/props/propTypes";
import type { Room } from "building-types/rooms/roomTypes";
import { WallType } from "building-types/walls/wallEnums";
import type { WallMassSystem } from "building-types/walls/wallSytemTypes";
import type { Wall } from "building-types/walls/wallTypes";
import type { RWTable, Table } from "common/data/table";
import { assertUnreachable } from "common/utils/assertUnreachable";
import {
  floorAssemblyDynamicFinishLayerOperatorNew,
  floorAssemblyExteriorFinishLayerOperatorNew,
  floorAssemblyInteriorFinishLayerOperatorNew,
} from "doc/elements/floor/floorFinish";
import type { MergedPropLibrary } from "doc/propTools";
import { propPlanOutline } from "doc/propTools";
import { aabb2Overlaps } from "geom/aabb2";
import { polygonIntersectSimple } from "geom/polygon";
import { polyLine2AABB2 } from "geom/polyLine2";
import type { PolyLine2, PolyLine2Polygon } from "geom/polyLine2Types";
import { computeThresholdsForSolidWall, computeThresholdsForVoidWall } from "../walls/wallOpeningThresholds";

export function filterCaseworkForOpenConnectedRooms(
  casework: Table<Casework>,
  openConnectedRoomIds: ReadonlySet<string>,
): Table<Casework> {
  const filtered: RWTable<Casework> = {};
  for (const caseworkId in casework) {
    const caseworkEntity = casework[caseworkId];
    if (openConnectedRoomIds.has(caseworkEntity.room.id) && caseworkEntity.floorMaterial) {
      filtered[caseworkId] = caseworkEntity;
    }
  }
  return filtered;
}

export function filterPropsForOpenConnectedRooms(
  props: Table<Prop>,
  openConnectedRoomIds: ReadonlySet<string>,
): Table<Prop> {
  const filtered: RWTable<Prop> = {};
  for (const propId in props) {
    const propEntity = props[propId];
    if (propEntity.room && openConnectedRoomIds.has(propEntity.room.id) && propEntity.floorMaterial) {
      filtered[propId] = propEntity;
    }
  }
  return filtered;
}

export function collectOpenConnectedRoomIds(
  levelIds: Iterable<string>,
  wallMasses: Table<WallMassSystem>,
): Set<string> {
  const openConnectedRoomIds = new Set<string>();
  for (const levelId of levelIds) {
    for (const roomSet of wallMasses[levelId].openConnectedRoomSets) {
      for (const room of roomSet) {
        openConnectedRoomIds.add(room.id);
      }
    }
  }
  return openConnectedRoomIds;
}

export async function buildPropOutlineCache(
  props: Table<Prop>,
  propCatalog: PropAttributeTable,
  mergedPropLibrary: MergedPropLibrary,
): Promise<Map<string, PolyLine2 | null>> {
  const cache = new Map<string, PolyLine2 | null>();
  const outlineTasks: Promise<void>[] = [];

  for (const propId in props) {
    const propEntity = props[propId];
    if (!propEntity.floorMaterial) {
      continue;
    }
    outlineTasks.push(
      propPlanOutline(propEntity, propCatalog, mergedPropLibrary).then((outline) => {
        cache.set(propId, outline);
      }),
    );
  }

  await Promise.all(outlineTasks);
  return cache;
}

export function makeCaseworkFloorFinishLayerOperators(
  casework: Table<Casework>,
  roomId: string,
  roomOutline: PolyLine2 | undefined,
): SourcedFloorAssemblyFinishLayerOperator[] {
  const operators: SourcedFloorAssemblyFinishLayerOperator[] = [];
  if (!roomOutline) {
    return operators;
  }

  for (const caseworkId in casework) {
    const caseworkEntity = casework[caseworkId];
    if (!caseworkEntity.floorMaterial) {
      continue;
    }
    const { pickHandle } = caseworkEntity;
    for (const outline of caseworkEntity.footprintOutlines) {
      let outlineArray: PolyLine2[] = [outline];
      if (caseworkEntity.room.id !== roomId) {
        const outlineAABB2 = polyLine2AABB2(outline);
        const roomOutlineAABB2 = polyLine2AABB2(roomOutline);
        const overlaps = aabb2Overlaps(outlineAABB2, roomOutlineAABB2);
        if (!overlaps) {
          continue;
        }
        outlineArray = polygonIntersectSimple(outline, roomOutline);
      }
      for (const outline of outlineArray) {
        operators.push(
          floorAssemblyInteriorFinishLayerOperatorNew("REPLACE_ALL", caseworkEntity.floorMaterial, pickHandle, outline),
        );
      }
    }
  }
  return operators;
}

export function makePropFloorFinishLayerOperators(
  props: Table<Prop>,
  roomId: string,
  roomOutline: PolyLine2 | undefined,
  propOutlinesById: ReadonlyMap<string, PolyLine2 | null>,
): SourcedFloorAssemblyFinishLayerOperator[] {
  const operators: SourcedFloorAssemblyFinishLayerOperator[] = [];
  if (!roomOutline) {
    return operators;
  }

  for (const propId in props) {
    const propEntity = props[propId];
    if (!propEntity.room || !propEntity.floorMaterial) {
      continue;
    }
    const propOutline = propOutlinesById.get(propId) ?? null;
    let propOutlineArray: PolyLine2[] = propOutline ? [propOutline] : [];
    if (propOutline && roomId !== propEntity.room?.id) {
      const propOutlineAABB2 = polyLine2AABB2(propOutline);
      const roomOutlineAABB2 = polyLine2AABB2(roomOutline);
      const overlaps = aabb2Overlaps(propOutlineAABB2, roomOutlineAABB2);
      if (!overlaps) {
        continue;
      }
      propOutlineArray = polygonIntersectSimple(propOutline, roomOutline);
    }
    const { pickHandle } = propEntity;
    for (const propOutline of propOutlineArray) {
      operators.push(
        floorAssemblyInteriorFinishLayerOperatorNew("REPLACE_ALL", propEntity.floorMaterial, pickHandle, propOutline),
      );
    }
  }
  return operators;
}

export function makeFloorFinishThresholdOperators(
  room: Room,
  roomWalls: Wall[],
  floorFinishes: SourcedFloorAssemblyFinishLayerOperator[] = [],
): SourcedFloorAssemblyFinishLayerOperator[] {
  const floorLayouts: SourcedFloorAssemblyFinishLayerOperator[] = [];

  const outlinesOut: PolyLine2Polygon[] = [];
  for (const wall of roomWalls) {
    if (wall.wallType === WallType.SOLID) {
      computeThresholdsForSolidWall(wall, room, outlinesOut);
    } else if (wall.wallType === WallType.VOID) {
      computeThresholdsForVoidWall(wall, outlinesOut);
    }
  }

  // use the topMost finish with an actual material,
  // and without a perimeter for the calculation of thesholds
  let floorFinishThreshold: SourcedFloorAssemblyFinishLayerOperator | undefined = undefined;
  const floorFinishesForThreshold = floorFinishes.filter((f) => !f.finishPerimeter && !isNoneTypeOperator(f));
  if (floorFinishesForThreshold.length) {
    floorFinishThreshold = floorFinishesForThreshold[floorFinishesForThreshold.length - 1];
  }

  if (floorFinishThreshold && outlinesOut.length) {
    switch (floorFinishThreshold.finishType) {
      case FloorFinishType.INTERIOR: {
        const { finishMaterial, srcHandle } = floorFinishThreshold;
        outlinesOut.forEach((outlines) => {
          outlines.forEach((p) => {
            floorLayouts.push(floorAssemblyInteriorFinishLayerOperatorNew("ADD", finishMaterial, srcHandle, p, true));
          });
        });
        break;
      }
      case FloorFinishType.EXTERIOR: {
        const { finishMaterial, srcHandle } = floorFinishThreshold;
        outlinesOut.forEach((outlines) => {
          outlines.forEach((p) => {
            floorLayouts.push(floorAssemblyExteriorFinishLayerOperatorNew("ADD", finishMaterial, srcHandle, p, true));
          });
        });
        break;
      }
      case FloorFinishType.DYNAMIC: {
        const { srcHandle } = floorFinishThreshold;
        outlinesOut.forEach((outlines) => {
          outlines.forEach((p) => {
            floorLayouts.push(floorAssemblyDynamicFinishLayerOperatorNew("ADD", srcHandle, p, true));
          });
        });
        break;
      }
      default: {
        assertUnreachable(floorFinishThreshold);
      }
    }
  }

  return floorLayouts;
}

export function isNoneTypeOperator(operator: FloorAssemblyFinishLayerOperator) {
  return (
    (operator.finishType === FloorFinishType.EXTERIOR && operator.finishMaterial === "NONE") ||
    (operator.finishType === FloorFinishType.INTERIOR && operator.finishMaterial === "NONE")
  );
}
